import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SkeletonBlock from "../common/SkeletonBlock";

const DUE_FALLBACK_STATES = new Set(["due", "overdue"]);
const DUE_DATE_FIELDS = [
  "dueAtMs",
  "dueDateMs",
  "dueAt",
  "dueDate",
  "nextDueAt",
  "nextDueDate",
  "scheduledAt",
  "scheduledFor",
];
const FREQUENCY_INTERVAL_DAYS = Object.freeze({
  daily: 1,
  "every-other-day": 2,
  "twice-weekly": 3.5,
  weekly: 7,
  fortnightly: 14,
  "two-weekly": 14,
  biweekly: 14,
  monthly: 30,
});

function padDatePart(value) {
  return String(value).padStart(2, "0");
}

function toDayKey(date) {
  if (!(date instanceof Date) || !Number.isFinite(date.getTime())) {
    return null;
  }

  return [
    date.getFullYear(),
    padDatePart(date.getMonth() + 1),
    padDatePart(date.getDate()),
  ].join("-");
}

function parseDayKey(dayKey) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dayKey || ""));

  if (!match) {
    return null;
  }

  const [, year, month, day] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day), 12);
  return Number.isFinite(date.getTime()) ? date : null;
}

function parseDateValue(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value === "number") {
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value === "string") {
    const dayOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());

    if (dayOnlyMatch) {
      return parseDayKey(value.trim());
    }

    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  }

  if (typeof value?.toDate === "function") {
    return parseDateValue(value.toDate());
  }

  if (Number.isFinite(value?.seconds)) {
    return parseDateValue(value.seconds * 1000);
  }

  return null;
}

function getTaskDueDate(task, todayKey, scheduleAnchorDate = null) {
  if (scheduleAnchorDate && getFrequencyIntervalDays(task?.frequency)) {
    return scheduleAnchorDate;
  }

  for (const field of DUE_DATE_FIELDS) {
    const date = parseDateValue(task?.[field]);

    if (date) {
      return date;
    }
  }

  if (DUE_FALLBACK_STATES.has(task?.state)) {
    return parseDayKey(todayKey);
  }

  return null;
}

function getFrequencyIntervalDays(frequency) {
  const normalized =
    typeof frequency === "string" ? frequency.trim().toLowerCase() : "";

  return FREQUENCY_INTERVAL_DAYS[normalized] || null;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function isDayKeyInRange(dayKey, rangeStartKey, rangeEndKey) {
  return (
    typeof dayKey === "string" &&
    dayKey >= rangeStartKey &&
    dayKey <= rangeEndKey
  );
}

function getCalendarEntryState(task, dayKey, todayKey) {
  if (dayKey > todayKey) {
    return "upcoming";
  }

  return task.state;
}

function getTaskDueEntries(
  task,
  todayKey,
  rangeStartKey,
  rangeEndKey,
  scheduleAnchorDate = null,
) {
  const dueDate = getTaskDueDate(task, todayKey, scheduleAnchorDate);

  if (!dueDate) {
    return [];
  }

  const entriesByDay = new Map();
  const addEntry = (date) => {
    const dayKey = toDayKey(date);

    if (!isDayKeyInRange(dayKey, rangeStartKey, rangeEndKey)) {
      return;
    }

    entriesByDay.set(dayKey, {
      dayKey,
      task,
      calendarState: getCalendarEntryState(task, dayKey, todayKey),
    });
  };
  const intervalDays = getFrequencyIntervalDays(task.frequency);
  let candidateDate = new Date(dueDate);
  let guard = 0;

  if (!intervalDays) {
    addEntry(candidateDate);
    return Array.from(entriesByDay.values());
  }

  while (toDayKey(candidateDate) < rangeStartKey && guard < 90) {
    candidateDate = addDays(candidateDate, intervalDays);
    guard += 1;
  }

  addEntry(candidateDate);

  candidateDate = addDays(candidateDate, intervalDays);

  while (toDayKey(candidateDate) <= rangeEndKey && guard < 180) {
    addEntry(candidateDate);
    candidateDate = addDays(candidateDate, intervalDays);
    guard += 1;
  }

  return Array.from(entriesByDay.values());
}

export function taskBoardHasSchedulableTasks(board, todayKey = toDayKey(new Date())) {
  const tasks = Array.isArray(board?.tasks) ? board.tasks : [];
  const scheduleAnchorDate = parseDateValue(
    board?.scheduleAnchorAt || board?.contextMetadata?.createdAt,
  );

  return tasks.some((task) =>
    Boolean(getTaskDueDate(task, todayKey, scheduleAnchorDate)),
  );
}

function getMonthStart(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1, 12);
}

function addMonths(date, amount) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1, 12);
}

function getWeekStartsOn(locale) {
  return String(locale || "").toLowerCase().startsWith("en-us") ? 0 : 1;
}

function buildMonthCells(monthDate, weekStartsOn) {
  const monthStart = getMonthStart(monthDate);
  const offset = (monthStart.getDay() - weekStartsOn + 7) % 7;
  const gridStart = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth(),
    1 - offset,
    12,
  );

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart);
    date.setDate(gridStart.getDate() + index);
    return date;
  });
}

function getWeekdayLabels(locale, weekStartsOn) {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const monday = new Date(2026, 0, 5, 12);

  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + ((weekStartsOn + index + 6) % 7));
    return formatter.format(date);
  });
}

function formatMonthLabel(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  }).format(date);
}

function formatPopupDateLabel(dayKey, locale) {
  const date = parseDayKey(dayKey);

  if (!date) {
    return "";
  }

  return new Intl.DateTimeFormat(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
}

function getTaskLabel(task, t) {
  if (task?.labelKey) {
    return t(task.labelKey, task.fallbackLabel);
  }

  return task?.fallbackLabel || task?.taskId || "";
}

function getStateIcon(state) {
  if (state === "completed") {
    return "check_circle";
  }

  if (state === "overdue") {
    return "error";
  }

  if (state === "due") {
    return "schedule";
  }

  if (state === "upcoming") {
    return "event_upcoming";
  }

  return "radio_button_unchecked";
}

function getStateTone(state) {
  if (state === "completed") {
    return {
      icon: "text-green-600",
      text: "text-green-700",
      day: "bg-green-50 text-green-800",
    };
  }

  if (state === "overdue") {
    return {
      icon: "text-red-600",
      text: "text-red-700",
      day: "bg-red-50 text-red-800",
    };
  }

  if (state === "due") {
    return {
      icon: "text-amber-600",
      text: "text-amber-700",
      day: "bg-amber-50 text-amber-800",
    };
  }

  if (state === "upcoming") {
    return {
      icon: "text-greyscale-3",
      text: "text-greyscale-3",
      day: "bg-greyscale-1 text-typography",
    };
  }

  return {
    icon: "text-greyscale-3",
    text: "text-greyscale-3",
    day: "bg-greyscale-1 text-typography",
  };
}

function getDayTone(tasks) {
  if (tasks.some((entry) => entry.calendarState === "overdue")) {
    return getStateTone("overdue");
  }

  if (tasks.some((entry) => entry.calendarState === "due")) {
    return getStateTone("due");
  }

  if (tasks.some((entry) => entry.calendarState === "upcoming")) {
    return getStateTone("upcoming");
  }

  if (tasks.some((entry) => entry.calendarState === "completed")) {
    return getStateTone("completed");
  }

  return getStateTone(null);
}

function TaskPopupRow({ entry, t }) {
  const { task, calendarState } = entry;
  const tone = getStateTone(calendarState);
  const label = getTaskLabel(task, t);
  const meta =
    calendarState === "upcoming"
      ? t("taskCalendar.upcoming")
      : t(`taskList.status.${calendarState}`, calendarState);
  const count =
    calendarState !== "upcoming" &&
    Number.isFinite(Number(task.completedCount)) &&
    Number.isFinite(Number(task.expectedCount))
      ? `${task.completedCount}/${task.expectedCount}`
      : null;
  const content = (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className={`material-symbols-outlined text-[20px] leading-none ${tone.icon}`}
        aria-hidden="true"
      >
        {getStateIcon(calendarState)}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold text-typography">{label}</p>
        <p className="!text-[11px] muted-text">{meta}</p>
      </div>
      {count ? (
        <span className={`shrink-0 text-xs font-semibold ${tone.text}`}>
          {count}
        </span>
      ) : null}
    </div>
  );
  const className =
    "block w-full max-w-full overflow-hidden rounded-xl bg-greyscale-1 px-2.5 py-1.5 text-left transition-colors";

  if (task.href) {
    return (
      <Link to={task.href} className={className}>
        {content}
      </Link>
    );
  }

  return <div className={className}>{content}</div>;
}

function getPopupPlacementClass(index) {
  const column = index % 7;
  const vertical = "top-full mt-1";

  if (column <= 2) {
    return `${vertical} left-0`;
  }

  if (column >= 4) {
    return `${vertical} right-0`;
  }

  return `${vertical} left-1/2 -translate-x-1/2`;
}

function TaskDueCalendar({ board, className = "" }) {
  const { t, i18n } = useTranslation("common");
  const locale = i18n.resolvedLanguage || i18n.language || "en-GB";
  const rootRef = useRef(null);
  const todayKey = useMemo(() => toDayKey(new Date()), []);
  const [displayMonth, setDisplayMonth] = useState(() =>
    getMonthStart(new Date()),
  );
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const weekStartsOn = getWeekStartsOn(locale);
  const tasks = Array.isArray(board?.tasks) ? board.tasks : [];
  const scheduleAnchorValue =
    board?.scheduleAnchorAt || board?.contextMetadata?.createdAt;
  const scheduleAnchorDate = useMemo(
    () => parseDateValue(scheduleAnchorValue),
    [scheduleAnchorValue],
  );
  const cells = useMemo(
    () => buildMonthCells(displayMonth, weekStartsOn),
    [displayMonth, weekStartsOn],
  );
  const rangeStartKey = toDayKey(cells[0]);
  const rangeEndKey = toDayKey(cells[cells.length - 1]);
  const taskEntries = useMemo(
    () =>
      tasks.flatMap((task) =>
        getTaskDueEntries(
          task,
          todayKey,
          rangeStartKey,
          rangeEndKey,
          scheduleAnchorDate,
        ),
      ),
    [rangeEndKey, rangeStartKey, scheduleAnchorDate, tasks, todayKey],
  );
  const hasSchedulableTasks = useMemo(
    () => taskBoardHasSchedulableTasks(board, todayKey),
    [board, todayKey],
  );
  const tasksByDay = useMemo(() => {
    const grouped = new Map();

    taskEntries.forEach((entry) => {
      const { dayKey } = entry;

      if (!grouped.has(dayKey)) {
        grouped.set(dayKey, []);
      }

      grouped.get(dayKey).push(entry);
    });

    return grouped;
  }, [taskEntries]);
  const weekdayLabels = useMemo(
    () => getWeekdayLabels(locale, weekStartsOn),
    [locale, weekStartsOn],
  );
  const handleTodayClick = () => {
    setDisplayMonth(getMonthStart(new Date()));
    setSelectedDayKey(null);
  };

  useEffect(() => {
    if (!selectedDayKey) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) {
        return;
      }

      setSelectedDayKey(null);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setSelectedDayKey(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedDayKey]);

  useEffect(() => {
    if (selectedDayKey && !tasksByDay.has(selectedDayKey)) {
      setSelectedDayKey(null);
    }
  }, [selectedDayKey, tasksByDay]);

  if (board?.loading) {
    return (
      <section
        className={["w-full card p-3 flex flex-col gap-2", className]
          .filter(Boolean)
          .join(" ")}
        aria-label={t("taskCalendar.ariaLabel")}
      >
        <SkeletonBlock className="h-5 w-28 rounded-xl" />
        <div className="grid grid-cols-7 gap-0.5">
          {Array.from({ length: 42 }, (_, index) => (
            <SkeletonBlock key={index} className="h-8 rounded-lg" />
          ))}
        </div>
      </section>
    );
  }

  if (!hasSchedulableTasks) {
    return null;
  }

  return (
    <section
      ref={rootRef}
      className={["relative w-full card p-3 flex flex-col gap-2", className]
        .filter(Boolean)
        .join(" ")}
      aria-label={t("taskCalendar.ariaLabel")}
    >
      <div className="flex items-center justify-between gap-1.5">
        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-greyscale-1"
          aria-label={t("taskCalendar.previousMonth")}
          onClick={() => setDisplayMonth((current) => addMonths(current, -1))}
        >
          <span className="material-symbols-outlined text-[22px]" aria-hidden="true">
            chevron_left
          </span>
        </button>

        <h2 className="min-w-0 flex-1 truncate text-center text-base font-bold text-typography">
          {formatMonthLabel(displayMonth, locale)}
        </h2>

        <button
          type="button"
          className="h-8 shrink-0 rounded-full border border-primary px-3 text-xs font-semibold text-primary transition-colors hover:bg-primary hover:text-greyscale-1"
          onClick={handleTodayClick}
        >
          {t("taskCalendar.today")}
        </button>

        <button
          type="button"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-greyscale-1"
          aria-label={t("taskCalendar.nextMonth")}
          onClick={() => setDisplayMonth((current) => addMonths(current, 1))}
        >
          <span className="material-symbols-outlined text-[22px]" aria-hidden="true">
            chevron_right
          </span>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-0.5">
        {weekdayLabels.map((label) => (
          <span
            key={label}
            className="text-center text-[10px] font-semibold uppercase muted-text"
          >
            {label}
          </span>
        ))}

        {cells.map((date, index) => {
          const dayKey = toDayKey(date);
          const dayEntries = tasksByDay.get(dayKey) || [];
          const hasTasks = dayEntries.length > 0;
          const isOutsideMonth = date.getMonth() !== displayMonth.getMonth();
          const isSelected = selectedDayKey === dayKey;
          const isToday = dayKey === todayKey;
          const tone = getDayTone(dayEntries);
          const selectedDateLabel = formatPopupDateLabel(dayKey, locale);
          const classNames = [
            "relative flex h-8 w-full items-center justify-center rounded-lg text-xs font-semibold transition-colors",
            isSelected ? "bg-primary text-greyscale-1 shadow" : "",
            !isSelected && hasTasks ? tone.day : "",
            !isSelected && !hasTasks ? "text-typography hover:bg-greyscale-1" : "",
            !isSelected && isOutsideMonth ? "text-greyscale-3 opacity-60" : "",
            !isSelected && isToday ? "ring-2 ring-primary/30" : "",
          ]
            .filter(Boolean)
            .join(" ");

          return (
            <div
              key={dayKey}
              className="relative"
              data-testid={`task-calendar-day-${dayKey}`}
            >
              <button
                type="button"
                className={classNames}
                aria-label={
                  hasTasks
                    ? t("taskCalendar.dayWithTasks", {
                        date: selectedDateLabel,
                        count: dayEntries.length,
                      })
                    : selectedDateLabel
                }
                aria-pressed={isSelected}
                onClick={() => setSelectedDayKey(hasTasks ? dayKey : null)}
              >
                <span>{date.getDate()}</span>
              </button>

              {isSelected && dayEntries.length > 0 ? (
                <div
                  role="dialog"
                  aria-label={t("taskCalendar.tasksDueOn", {
                    date: selectedDateLabel,
                  })}
                  className={[
                    "absolute z-20 w-[min(224px,calc(100vw-3rem))] rounded-2xl bg-card p-2.5 shadow-lg ring-1 ring-greyscale-3/20",
                    getPopupPlacementClass(index),
                  ].join(" ")}
                >
                  <h3 className="mb-1.5 text-xs font-bold text-typography">
                    {t("taskCalendar.tasksDueOn", { date: selectedDateLabel })}
                  </h3>
                  <div
                    className="flex max-h-48 flex-col gap-1.5 overflow-y-auto pr-1"
                    data-testid="task-calendar-popup-list"
                  >
                    {dayEntries.map((entry) => (
                      <TaskPopupRow
                        key={`${entry.dayKey}:${entry.task.id || `${entry.task.kind}:${entry.task.taskId}`}`}
                        entry={entry}
                        t={t}
                      />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export default memo(TaskDueCalendar);
